import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MapPin, Image, Megaphone, Download, Trash2, MessageSquareWarning, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, setUser, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [consent, setConsent] = useState(user?.consents || { location: false, photos: false, marketing: false });
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");

  const updateConsent = async (key, val) => {
    const next = { ...consent, [key]: val };
    setConsent(next);
    try { await api.put("/consent", { [key]: val }); await refresh(); toast.success(`${key} consent ${val ? "granted" : "withdrawn"}`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const exportData = async () => {
    const { data } = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "my-saathly-data.json"; a.click();
    toast.success("Your data has been exported");
  };
  const requestDeletion = async () => {
    await api.post("/account/deletion-request");
    toast.success("Deletion request logged. You'll be logged out.");
    setTimeout(() => { logout(); navigate("/"); }, 1500);
  };
  const submitGrievance = async () => {
    if (!subject || !desc) return toast.error("Add a subject and description");
    try {
      const { data } = await api.post("/grievance", { subject, description: desc });
      toast.success(data.message); setSubject(""); setDesc("");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const items = [
    { k: "location", icon: MapPin, t: "Location", d: "Distance-based matching & safety." },
    { k: "photos", icon: Image, t: "Photos", d: "Profile & verification photos." },
    { k: "marketing", icon: Megaphone, t: "Marketing", d: "Offers via email/WhatsApp." },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal">Settings & Privacy</h1>
        <p className="text-charcoal/60 mt-2">Your data, your rights — under the DPDP Act 2023.</p>

        <section className="bg-white rounded-2xl border border-border p-6 mt-8">
          <h2 className="font-display text-xl font-bold text-charcoal flex items-center gap-2"><Shield className="w-5 h-5 text-sage" /> Consent</h2>
          <p className="text-sm text-charcoal/60 mb-4">Withdraw any consent at any time.</p>
          {items.map((c) => (
            <div key={c.k} className="flex items-center justify-between bg-sand rounded-xl p-4 mb-3">
              <div className="flex items-center gap-3"><c.icon className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
                <div><p className="font-semibold text-charcoal text-sm">{c.t}</p><p className="text-xs text-charcoal/60">{c.d}</p></div></div>
              <Switch data-testid={`setting-consent-${c.k}`} checked={consent[c.k]} onCheckedChange={(v) => updateConsent(c.k, v)} className="data-[state=checked]:bg-sage" />
            </div>
          ))}
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 mt-6">
          <h2 className="font-display text-xl font-bold text-charcoal">Your data</h2>
          <div className="flex gap-3 mt-4 flex-wrap">
            <Button data-testid="export-data" variant="outline" onClick={exportData} className="rounded-full border-charcoal/20"><Download className="w-4 h-4 mr-1" /> Export my data</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button data-testid="delete-account" variant="ghost" className="rounded-full text-signal"><Trash2 className="w-4 h-4 mr-1" /> Request deletion</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>We'll erase your data per our retention policy, except records we must keep by law (e.g. financial records). This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel className="rounded-full">Keep account</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-deletion" onClick={requestDeletion} className="rounded-full bg-signal hover:bg-signal/90">Request deletion</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-border p-6 mt-6">
          <h2 className="font-display text-xl font-bold text-charcoal flex items-center gap-2"><MessageSquareWarning className="w-5 h-5 text-terracotta" /> Raise a grievance</h2>
          <p className="text-sm text-charcoal/60 mb-4">To our Grievance Officer (grievance@saathly.in). Acknowledged promptly, resolved within 15 days (verify with counsel).</p>
          <Label className="text-sm font-semibold">Subject</Label>
          <Input data-testid="grievance-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 rounded-xl mb-3" placeholder="What's this about?" />
          <Label className="text-sm font-semibold">Description</Label>
          <Textarea data-testid="grievance-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1 rounded-xl mb-4" placeholder="Describe your concern..." />
          <Button data-testid="submit-grievance" onClick={submitGrievance} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">Submit grievance</Button>
        </section>
      </div>
    </div>
  );
}
